import React from "react";
import { createRoot } from "react-dom/client";
import { Bell, Send } from "lucide-react";
import { httpClient } from "../../HttpClient";
import { showStatusToast } from "../../components/ui/Toast";
import { MISSIONS_CODEES, codesEffectifs } from "./MissionsRecurrentes";

// Modification des missions (ou de leurs codes) d'un dossier déjà actif,
// depuis le formulaire Modifier — demande d'Anthony du 19/10/2026 :
//   1. au clic sur « Enregistrer », la fenêtre « Notifier les collaborateurs ? »
//      s'ouvre par-dessus le formulaire (demanderNotificationMissions) ;
//   2. « Non » ou « Oui, notifier » ferme les deux fenêtres et lance
//      l'enregistrement (toast de l'enregistrement) ;
//   3. si « Oui » : une fois l'enregistrement confirmé, mail à l'équipe du
//      dossier (envoyerNotificationMissions, backend
//      notificationMissionsController, copie cachée) et toast de l'envoi.

const EQUIPE = [
  ["email_expertComptable", "Expert-comptable"],
  ["email_superviseur", "Superviseur"],
  ["email_collaborateur", "Collaborateur"],
  ["email_collaborateur_niv2", "Collaborateur niv. 2"],
  ["email_social", "Responsable social"],
  ["email_juridique", "Responsable juridique"],
];

// Missions dont la case ou les codes ont changé.
export function missionsModifiees(avant, apres) {
  return MISSIONS_CODEES.filter((m) => codesEffectifs(avant, m).join() !== codesEffectifs(apres, m).join());
}

// Équipe du dossier (même règle que le serveur) : une ligne par adresse.
function equipeDuDossier(client, users) {
  const parEmail = new Map();
  EQUIPE.forEach(([champ, role]) => {
    const email = String(client?.[champ] || "").trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    parEmail.set(email, [...(parEmail.get(email) || []), role]);
  });
  return [...parEmail.entries()].map(([email, roles]) => {
    const u = (users || []).find((x) => String(x.email || "").toLowerCase() === email);
    return { email, roles, nom: u ? `${u.name || ""} ${u.lastName || ""}`.trim() : email };
  });
}

const libelleCodes = (client, m, parCode) => {
  const codes = codesEffectifs(client, m);
  return codes.length ? codes.map((c) => `${c}${parCode[c] ? ` — ${parCode[c].libelle}` : ""}`) : ["Non confiée"];
};

function FenetreNotification({ clientNom, avant, apres, users, missions, repondre }) {
  const parCode = Object.fromEntries((missions || []).map((e) => [e.code, e]));
  const changees = missionsModifiees(avant, apres);
  const equipe = equipeDuDossier(apres, users);
  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/40 p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="notif-missions-titre" className="w-full max-w-[560px] rounded-xl border border-slate-200 bg-white shadow-xl">
        <div className="border-b border-slate-100 px-5 py-4">
          <h3 id="notif-missions-titre" className="flex items-center gap-2 text-base font-bold text-slate-900">
            <Bell className="h-4 w-4 text-[#840040]" /> Notifier les collaborateurs ?
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Les missions du dossier <b className="text-slate-700">{clientNom}</b> vont être modifiées.
          </p>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {changees.map((m) => (
              <div key={m.champ} className="grid grid-cols-[90px_1fr_1fr] gap-2 px-3 py-2 text-xs">
                <span className="font-semibold text-slate-800">{m.libelle}</span>
                <span className="text-slate-400 line-through decoration-slate-300">{libelleCodes(avant, m, parCode).join(" · ")}</span>
                <span className="font-medium text-slate-900">{libelleCodes(apres, m, parCode).join(" · ")}</span>
              </div>
            ))}
          </div>

          <div>
            <p className="mb-1.5 text-xs font-semibold text-slate-500">
              {equipe.length ? "Le mail sera envoyé à l'équipe du dossier :" : "Aucun collaborateur avec une adresse email n'est lié à ce dossier."}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {equipe.map((d) => (
                <span key={d.email} title={d.email} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-700">
                  <b className="font-semibold">{d.nom}</b> <span className="text-slate-400">· {d.roles.join(", ")}</span>
                </span>
              ))}
            </div>
          </div>
          <p className="text-[11px] text-slate-400">Dans les deux cas, les modifications de la fiche sont enregistrées.</p>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
          <button onClick={() => repondre(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
            Non
          </button>
          <button
            onClick={() => repondre(true)}
            disabled={!equipe.length}
            className="inline-flex items-center gap-2 rounded-lg bg-[#840040] px-5 py-2 text-sm font-semibold text-white hover:bg-[#6a0033] disabled:opacity-60"
          >
            <Send className="h-4 w-4" /> Oui, notifier
          </button>
        </div>
      </div>
    </div>
  );
}

// Au clic sur « Enregistrer » : demande s'il faut prévenir l'équipe. Renvoie
// une Promise : true (notifier), false (non), ou null si la question ne se pose
// pas (dossier pas encore actif, ou missions inchangées).
export function demanderNotificationMissions({ clientNom, avant, apres, users, missions }) {
  const statutAvant = avant?.status || "Actif";
  if (statutAvant !== "Actif" || !missionsModifiees(avant, apres).length) return Promise.resolve(null);
  return new Promise((resolve) => {
    const noeud = document.createElement("div");
    document.body.appendChild(noeud);
    const root = createRoot(noeud);
    const repondre = (oui) => {
      root.unmount();
      noeud.remove();
      resolve(oui);
    };
    root.render(<FenetreNotification clientNom={clientNom} avant={avant} apres={apres} users={users} missions={missions} repondre={repondre} />);
  });
}

// Après l'enregistrement confirmé : mail à l'équipe du dossier + toast.
export async function envoyerNotificationMissions({ clientId, avant }) {
  const toast = showStatusToast({ status: "loading", titre: "Notification des collaborateurs…", message: "Envoi du mail à l'équipe du dossier." });
  try {
    const avantMissions = Object.fromEntries(MISSIONS_CODEES.flatMap((m) => [[m.champ, avant?.[m.champ] ?? "non"], [m.codes, avant?.[m.codes] || []]]));
    const { data } = await httpClient.post(`https://backend-myalfa.vercel.app/api/clients/${clientId}/notifier-missions`, { avant: avantMissions });
    const noms = (data?.envoyeA || []).map((d) => d.nom);
    toast.update({
      status: "success",
      titre: "Mail envoyé",
      message: `${noms.length} collaborateur${noms.length > 1 ? "s ont" : " a"} été prévenu${noms.length > 1 ? "s" : ""} : ${noms.join(", ")}.`,
    });
  } catch (err) {
    toast.update({ status: "error", titre: "Mail non envoyé", message: err?.response?.data?.error || "La notification des collaborateurs n'a pas pu être envoyée." });
  }
}
